import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Button, DesignV2Scope, Menu, Skeleton, StickyPageHeader } from "../../design-v2";
import useAuthBootstrap from "../../auth/useAuthBootstrap";
import { getUserSettings } from "../../services/api";
import SettingsDrawer from "../settings/SettingsDrawer";
import { SETTINGS_SCREEN_IDS } from "../settings/settingsNavigation";
import * as defaultApi from "./api/coachApi";
import CheckinSheet from "./components/CheckinSheet";
import CoachComposer from "./components/CoachComposer";
import { CoachConsent, CoachOff, CoachUnavailable } from "./components/CoachGate";
import CoachSettings from "./components/CoachSettings";
import CoachThread from "./components/CoachThread";
import { CheckinIcon, MoreIcon } from "./components/coachIcons";
import { formatResetsAt } from "./domain/coachFormat";
import { TURN_PHASES } from "./domain/coachThread";
import useCoachConversation from "./hooks/useCoachConversation";
import useCoachHome from "./hooks/useCoachHome";
import "./coach.css";

/*
 * /ai — Coach IA phase 2 (contract coach-phase-2.md §10.2 B4).
 *
 * The conversation is the screen. The header's one menu holds the weekly
 * check-in and Coach settings (memory, data controls), so nothing
 * administrative sits in the thread. The coach acts on nothing in phase 2: its
 * links only navigate (Training Profile in the settings drawer, Program),
 * never to a builder.
 */

const TRAINING_PROFILE_SCREEN = Object.freeze({ id: SETTINGS_SCREEN_IDS.TRAINING_PROFILE_MENU });
const LOW_REMAINING = 5;

function checkinDismissKey(userId, weekStart) {
  return `lz.coach.checkinDismissed.v1:${userId}:${weekStart}`;
}

// "Not now" on the activation screen: remembered per user on this device so
// the screen does not come back on every visit. Nothing is sent: consent stays
// required until the athlete enables the coach.
function notNowKey(userId) {
  return `lz.coach.activationDeferred.v1:${userId}`;
}

function readDismissed(key) {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function useProfileAvatar() {
  const [avatar, setAvatar] = useState({ url: null, sex: null });
  useEffect(() => {
    let active = true;
    getUserSettings()
      .then((response) => {
        if (active) setAvatar({ url: response?.account?.profile?.avatar?.url || null, sex: response?.account?.profile?.sex || null });
      })
      .catch(() => {
        // the placeholder stays
      });
    return () => {
      active = false;
    };
  }, []);
  return avatar;
}

function CoachHeader({ checkinDue, onCheckin, onSettings }) {
  const avatar = useProfileAvatar();
  return (
    <StickyPageHeader className="lz-coach-header">
      <Avatar src={avatar.url} sex={avatar.sex} size={40} className="lz-coach-header__avatar" />
      <h1 className="lz-coach-header__title">AI Coach</h1>
      <div className="lz-coach-header__actions">
        <Menu
          trigger={
            <button type="button" className="lz-coach-icon-button" aria-label="Coach menu">
              <MoreIcon />
            </button>
          }
          menuClassName="lz-coach-menu"
        >
          {onCheckin ? (
            <button type="button" role="menuitem" onClick={onCheckin}>
              Weekly check-in
              {checkinDue ? <span className="lz-coach-menu__badge">Due</span> : null}
            </button>
          ) : null}
          <button type="button" role="menuitem" onClick={onSettings}>
            Settings
          </button>
        </Menu>
      </div>
    </StickyPageHeader>
  );
}

// One small line above the field, once a week, easy to dismiss.
function CheckinPrompt({ onStart, onDismiss }) {
  return (
    <div className="lz-coach-checkin-prompt">
      <button type="button" className="lz-coach-checkin-prompt__start" onClick={onStart}>
        <CheckinIcon />
        Weekly check-in
        <span className="lz-coach-checkin-prompt__meta">1 min</span>
      </button>
      <button type="button" className="lz-coach-checkin-prompt__dismiss" aria-label="Not now" onClick={onDismiss}>
        ×
      </button>
    </div>
  );
}

function EmptyConversation({ cycle }) {
  return (
    <div className="lz-coach-empty">
      <p className="lz-coach-eyebrow">{cycle ? `${cycle.name} · Week ${cycle.weekNumber} of ${cycle.totalWeeks}` : "Your coach"}</p>
      <h2 className="lz-coach-empty__title">How's training going?</h2>
      <p className="lz-coach-empty__body">
        Ask about your program, tell your coach how a session felt, or share what works for you. It knows your Training Profile
        and your workout history.
      </p>
    </div>
  );
}

// An account exempt from the daily limit (limits.unlimited) never reaches it and never sees a count.
function dailyLimitReached(limits) {
  if (!limits || limits.unlimited === true || typeof limits.dailyMessages !== "number") return false;
  return limits.usedToday >= limits.dailyMessages;
}

function blockedCopy(home) {
  if (home?.budget?.state === "EXHAUSTED") {
    return "Your coach is taking a break until next month. Your conversation and memories are safe.";
  }
  const limits = home?.limits;
  if (dailyLimitReached(limits)) {
    return `You've used today's ${limits.dailyMessages} messages. Your coach is back ${formatResetsAt(limits.resetsAt)}.`;
  }
  return null;
}

function remainingHint(limits) {
  if (!limits || limits.unlimited === true || typeof limits.dailyMessages !== "number") return null;
  const remaining = limits.dailyMessages - limits.usedToday;
  if (remaining <= 0 || remaining > LOW_REMAINING) return null;
  return `${remaining} ${remaining === 1 ? "message" : "messages"} left today`;
}

// `timing` only shortens the reply polling in tests.
export default function CoachScreen({ api = defaultApi, timing = null }) {
  const navigate = useNavigate();
  const { user } = useAuthBootstrap();
  const userId = user?.id || null;
  const coachHome = useCoachHome({ api });
  const { home, status: homeStatus, reload: reloadHome } = coachHome;
  const [sheet, setSheet] = useState(null);
  const [coachSettingsScreen, setCoachSettingsScreen] = useState("root");
  const [settingsScreen, setSettingsScreen] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [dismissedCheckin, setDismissedCheckin] = useState(false);
  const [deferred, setDeferred] = useState(false);

  const available = home?.availability === "AVAILABLE";
  const consented = available && home.consent?.required === false;

  const conversation = useCoachConversation({
    enabled: consented,
    userId,
    pendingReply: home?.pendingReply || null,
    api,
    ...timing,
    onLimits: coachHome.setLimits,
    onBudgetExhausted: coachHome.setBudgetExhausted,
    onHomeStale: () => reloadHome({ quiet: true }),
    onMemoriesChanged: (memory) => coachHome.adjustMemoryCount(memory.savedIds?.length || 0),
  });

  // The daily limit lifts at resetsAt: read /home again then.
  const resetsAt = home?.limits?.resetsAt;
  const limitReached = dailyLimitReached(home?.limits);
  useEffect(() => {
    if (!limitReached || !resetsAt) return undefined;
    const delay = new Date(resetsAt).getTime() - Date.now();
    if (!(delay > 0 && delay < 24 * 60 * 60 * 1000)) return undefined;
    const timer = setTimeout(() => reloadHome({ quiet: true }), delay + 1000);
    return () => clearTimeout(timer);
  }, [limitReached, resetsAt, reloadHome]);

  const weekStart = home?.checkin?.weekStart;
  useEffect(() => {
    setDismissedCheckin(Boolean(userId && weekStart && readDismissed(checkinDismissKey(userId, weekStart))));
  }, [userId, weekStart]);

  useEffect(() => {
    setDeferred(Boolean(userId && readDismissed(notNowKey(userId))));
  }, [userId]);

  const setActivationDeferred = useCallback((value) => {
    setDeferred(value);
    try {
      if (value) window.localStorage.setItem(notNowKey(userId), "1");
      else window.localStorage.removeItem(notNowKey(userId));
    } catch {
      // the activation screen shows again next time
    }
  }, [userId]);

  const dismissCheckin = useCallback(() => {
    setDismissedCheckin(true);
    try {
      window.localStorage.setItem(checkinDismissKey(userId, weekStart), "1");
    } catch {
      // the prompt comes back on the next visit
    }
  }, [userId, weekStart]);

  const openLink = useCallback((target) => {
    if (target?.type === "route") navigate(target.to);
    else if (target?.type === "trainingProfile") setSettingsScreen(TRAINING_PROFILE_SCREEN);
  }, [navigate]);

  const handleRetry = useCallback(async (clientMessageId) => {
    const result = await conversation.retry(clientMessageId);
    if (result?.restoreText) setPrefill({ text: result.restoreText, at: Date.now() });
  }, [conversation]);

  const waiting = useMemo(
    () => conversation.items.some((item) => item.kind === "user" && (item.status === TURN_PHASES.SENDING || item.status === TURN_PHASES.WAITING)),
    [conversation.items]
  );
  const exhaustedText = useMemo(() => {
    const last = [...conversation.items].reverse().find((item) => item.kind === "user");
    return last?.status === TURN_PHASES.EXHAUSTED ? last.text : null;
  }, [conversation.items]);

  const closeSheet = () => setSheet(null);
  const openCoachSettings = (screen = "root") => {
    setCoachSettingsScreen(screen);
    setSheet("settings");
  };
  // Consent is gone with it: /home shows the consent screen again, and the
  // conversation reloads from scratch once the athlete agrees again.
  const afterDataGone = useCallback(async () => {
    setSheet(null);
    await reloadHome({ quiet: true });
  }, [reloadHome]);

  let body;
  if (homeStatus === "loading" && !home) {
    body = (
      <div className="lz-coach-loading" aria-busy="true" aria-label="Loading your coach">
        <Skeleton height="3rem" width="70%" />
        <Skeleton height="2.5rem" width="55%" className="lz-coach-loading__right" />
        <Skeleton height="4rem" width="80%" />
      </div>
    );
  } else if (!home) {
    body = <CoachUnavailable availability="ERROR" onRetry={() => reloadHome()} />;
  } else if (!available) {
    body = <CoachUnavailable availability={home.availability} onRetry={() => reloadHome()} onManageData={() => openCoachSettings()} />;
  } else if (!consented) {
    body = (
      deferred ? (
        <CoachOff onEnable={() => setActivationDeferred(false)} />
      ) : (
        <CoachConsent
          consent={home.consent}
          onNotNow={() => setActivationDeferred(true)}
          onAccept={async ({ version }) => {
            try {
              coachHome.applySettings(await api.updateCoachSettings({ consent: { accepted: true, version } }));
              setActivationDeferred(false);
              return true;
            } catch (error) {
              if (error?.code === "COACH_CONSENT_VERSION_MISMATCH") await reloadHome({ quiet: true });
              return false;
            }
          }}
        />
      )
    );
  } else {
    const showPrompt = home.checkin?.due && !dismissedCheckin;
    body = (
      <>
        <div className="lz-coach__conversation">
          {conversation.loadStatus === "error" ? (
            <div className="lz-coach-sheet__state">
              <p>Couldn't load your conversation.</p>
              <Button variant="ghost" size="sm" onClick={conversation.reload}>
                Try again
              </Button>
            </div>
          ) : null}
          {conversation.loadStatus === "ready" && !conversation.items.length ? <EmptyConversation cycle={home.cycle} /> : null}
          <CoachThread
            items={conversation.items}
            waiting={waiting}
            hasOlder={conversation.hasOlder}
            loadingOlder={conversation.loadingOlder}
            onLoadOlder={conversation.loadOlder}
            onRetry={handleRetry}
            retryDisabled={conversation.busy}
            onOpenLink={openLink}
          />
          {exhaustedText ? (
            <div className="lz-coach-thread__nudge">
              <button type="button" className="lz-coach-text-button" onClick={() => setPrefill({ text: exhaustedText, at: Date.now() })}>
                Write it again
              </button>
            </div>
          ) : null}
        </div>
        {showPrompt ? <CheckinPrompt onStart={() => setSheet("checkin")} onDismiss={dismissCheckin} /> : null}
        <CoachComposer
          busy={conversation.busy}
          blocked={blockedCopy(home)}
          hint={remainingHint(home.limits)}
          prefill={prefill}
          onSend={conversation.send}
        />
      </>
    );
  }

  return (
    <DesignV2Scope className="lz-coach">
      <CoachHeader
        checkinDue={Boolean(home?.checkin?.due)}
        onCheckin={consented ? () => setSheet("checkin") : null}
        onSettings={() => openCoachSettings()}
      />
      <div className="lz-coach__content">{body}</div>

      <CoachSettings
        open={sheet === "settings"}
        onClose={() => {
          closeSheet();
          conversation.refreshLatest();
        }}
        initialScreen={coachSettingsScreen}
        home={home}
        rightsOnly={!consented}
        api={api}
        onSettings={coachHome.applySettings}
        onDataGone={afterDataGone}
        onCountChange={coachHome.setMemoryCount}
        onSourceErased={conversation.markErased}
        onHomeStale={() => reloadHome({ quiet: true })}
        onOpenAppSettings={() => {
          setSheet(null);
          setSettingsScreen({ id: SETTINGS_SCREEN_IDS.ROOT });
        }}
      />
      <CheckinSheet
        open={sheet === "checkin"}
        onClose={closeSheet}
        checkin={home?.checkin}
        api={api}
        onHomeStale={() => reloadHome({ quiet: true })}
        onSubmitted={(response) => {
          coachHome.markCheckinSubmitted(response?.event?.id || response?.alreadySubmittedEventId);
          if (response?.event) {
            conversation.addEvents([response.event]);
            setSheet(null);
          }
        }}
      />
      <SettingsDrawer isOpen={Boolean(settingsScreen)} initialScreen={settingsScreen} onClose={() => setSettingsScreen(null)} />
    </DesignV2Scope>
  );
}
